import React, { useEffect, useState } from 'react';

const time = (value) => value ? new Date(value).toLocaleString() : 'Not available';
const statusClass = {
  ready: 'bg-emerald-50 text-emerald-700',
  received: 'bg-blue-50 text-blue-700',
  ignored: 'bg-slate-100 text-slate-600',
  error: 'bg-red-50 text-red-700'
};

const ReceivedPlayerEvents = ({ initialEvents = [], initialListener }) => {
  const [events, setEvents] = useState(initialEvents);
  const [listener, setListener] = useState(initialListener);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let stopped = false;
    let timer;
    let controller;
    const refresh = async () => {
      controller = new AbortController();
      try {
        const response = await fetch('/events/received', {
          headers: { Accept: 'application/json' }, signal: controller.signal
        });
        if (!response.ok) throw new Error('Could not refresh received events.');
        const data = await response.json();
        if (!stopped) {
          setEvents(data.events);
          setListener(data.listener);
          setError('');
        }
      } catch (e) { if (!stopped) setError(e.message); }
      finally { if (!stopped) timer = setTimeout(refresh, 10000); }
    };
    timer = setTimeout(refresh, 10000);
    return () => { stopped = true; clearTimeout(timer); controller?.abort(); };
  }, []);

  const healthy = listener?.healthy && Date.now() - new Date(listener.heartbeat_at).getTime() < 90000;
  const selected = events.find((event) => event.id === selectedId);

  return (
    <section className='mt-10'>
      <div className='flex items-start justify-between flex-wrap gap-3 mb-4'>
        <div>
          <h2 className='text-lg font-semibold text-slate-900'>Received player events</h2>
          <p className='text-sm text-slate-500 mt-1'>Latest 50 registrations, deposits and withdrawals.</p>
        </div>
        <div className='text-sm text-right'>
          <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${healthy
            ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
            <span className={`w-2 h-2 rounded-full ${healthy ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {healthy ? 'Listener connected' : listener?.status === 'error' ? 'Listener connection error' : 'Listener offline'}
          </span>
          {listener && <p className='text-xs text-slate-500 mt-1'>Last heartbeat: {time(listener.heartbeat_at)}</p>}
          {listener?.error_code && <p className='text-xs text-red-600 mt-1'>{listener.error_code}</p>}
        </div>
      </div>
      {error && <p role='alert' className='text-sm text-red-600 mb-3'>{error}</p>}
      <div className='bg-white border border-slate-200 rounded-xl overflow-x-auto'>
        <table className='w-full text-sm text-left'>
          <thead className='bg-slate-50 text-slate-500'>
            <tr>{['Event Type', 'Player ID', 'Source ID', 'Event time', 'Received', 'Status'].map((label) =>
              <th key={label} scope='col' className='px-4 py-3 font-medium whitespace-nowrap'>{label}</th>)}</tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {events.map((event) => <tr key={event.id}>
              <td className='px-4 py-3'>
                <button className='text-blue-600 font-mono text-left' onClick={() => setSelectedId(event.id)}>
                  {event.event_type || event.source_kind}
                </button>
              </td>
              <td className='px-4 py-3'>{event.player_id || 'Not available'}</td>
              <td className='px-4 py-3 font-mono'>{event.source_id}</td>
              <td className='px-4 py-3 whitespace-nowrap'>{time(event.occurred_at)}</td>
              <td className='px-4 py-3 whitespace-nowrap'>{time(event.received_at)}</td>
              <td className='px-4 py-3'>
                <span className={`text-xs px-2 py-1 rounded ${statusClass[event.status] || statusClass.received}`}>
                  {event.status}
                </span>
              </td>
            </tr>)}
            {events.length === 0 && <tr><td colSpan={6} className='px-4 py-10 text-center text-slate-500'>
              No player events received yet. Start the listener to receive new registrations, deposits and withdrawals.
            </td></tr>}
          </tbody>
        </table>
      </div>
      {selected && <div className='mt-4 border border-slate-200 rounded-xl bg-slate-50 p-4'>
        <div className='flex justify-between items-center gap-3 mb-2'>
          <h3 className='font-medium text-slate-900'>Event parameters: {selected.event_type || selected.source_kind}</h3>
          <button className='text-sm text-slate-500' onClick={() => setSelectedId(null)}>Close parameters</button>
        </div>
        {selected.status === 'ignored' && <p className='text-sm text-slate-500 mb-2'>This payment status does not trigger a scenario.</p>}
        {selected.error_code && <p className='text-sm text-red-600 mb-2'>
          {selected.error_code} (attempts: {selected.attempts})
        </p>}
        <pre className='text-xs text-slate-700 overflow-auto'>{JSON.stringify(selected.parameters, null, 2)}</pre>
      </div>}
    </section>
  );
};

export default ReceivedPlayerEvents;
